import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

const stagger = {
  parent: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
  },
  child: {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
  },
};

const securityCards = [
  {
    title: 'SOC 2 Compliant',
    description: 'Independently audited security controls meeting the highest standards',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    borderHover: 'hover:border-blue-200 dark:hover:border-blue-700',
  },
  {
    title: 'End-to-End Encryption',
    description: '256-bit AES encryption for data at rest and TLS 1.3 for data in transit',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    borderHover: 'hover:border-emerald-200 dark:hover:border-emerald-700',
  },
  {
    title: 'Role-Based Access',
    description: 'Granular permissions and multi-tenant isolation for complete data segregation',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    borderHover: 'hover:border-purple-200 dark:hover:border-purple-700',
  },
  {
    title: 'Continuous Monitoring',
    description: '24/7 threat detection, automated backups, and 99.9% uptime SLA',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    borderHover: 'hover:border-amber-200 dark:hover:border-amber-700',
  },
];

const trustBadges = [
  { label: 'ISO 27001', color: 'bg-blue-50 text-blue-700 border-blue-200/60' },
  { label: 'GDPR',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60' },
  { label: 'SOC 2',    color: 'bg-purple-50 text-purple-700 border-purple-200/60' },
  { label: 'HIPAA',    color: 'bg-amber-50 text-amber-700 border-amber-200/60' },
];

const SecuritySection = () => (
  <SectionWrapper id="security" bgClass="bg-slate-100 dark:bg-[#111827]" padding="py-10 md:py-12" className="relative">
    <motion.div
      variants={stagger.parent}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      className="flex flex-col items-center"
    >
      {/* Pill badge */}
      <motion.div variants={stagger.child}>
        <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200/60 dark:border-blue-700/60 px-4 py-1.5 text-xs font-semibold tracking-wide text-blue-700 dark:text-blue-300 uppercase">
          <Shield className="h-3.5 w-3.5" />
          Enterprise Security
        </span>
      </motion.div>

      {/* Headline */}
      <motion.h2
        variants={stagger.child}
        className="mt-6 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white text-center"
      >
        Your Data,{' '}
        <span className="bg-gradient-to-r from-emerald-500 to-teal-400 bg-clip-text text-transparent">
          Fully Protected
        </span>
      </motion.h2>

      {/* Subtitle */}
      <motion.p
        variants={stagger.child}
        className="mt-4 max-w-2xl text-center text-base md:text-lg text-gray-500 dark:text-slate-400 leading-relaxed"
      >
        Enterprise-grade security and compliance built into every layer
      </motion.p>

      {/* Cards */}
      <motion.div
        variants={stagger.child}
        className="mt-10 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
      >
        {securityCards.map((card) => (
          <motion.div
            key={card.title}
            variants={stagger.child}
            className={`flex flex-col rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 transition-all duration-300 hover:shadow-lg ${card.borderHover}`}
          >
            {/* Check-circle icon — matches ComparisonSection style */}
            <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${card.iconBg}`}>
              <Check className={`w-4 h-4 ${card.iconColor}`} strokeWidth={2.5} />
            </span>

            <h3 className="mt-4 text-base font-bold text-gray-900 dark:text-white">{card.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-slate-400">{card.description}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Trust badges */}
      <motion.div
        variants={stagger.child}
        className="mt-8 flex flex-wrap items-center justify-center gap-3"
      >
        <span className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-slate-500 mr-2">
          Certifications
        </span>
        {trustBadges.map((badge) => (
          <span
            key={badge.label}
            className={`inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-bold tracking-wide ${badge.color} transition-all duration-300 hover:shadow-sm hover:scale-105`}
          >
            {badge.label}
          </span>
        ))}
      </motion.div>
    </motion.div>
  </SectionWrapper>
);

export default SecuritySection;
